import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Clock, Download, Home } from "lucide-react";
import api from "../../lib/api";
import SEO from "../../components/SEO";
import { PageHero } from "../../components/Bits";

const MAX_POLLS = 8;        // ~24s at 3s intervals — bounded, never indefinite
const POLL_MS = 3000;

export default function DonateStatus() {
  const [params] = useSearchParams();
  const donNo = params.get("don_no") || "";
  const [st, setSt] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [checking, setChecking] = useState(false);
  const timer = useRef(null);

  const fetchStatus = async () => {
    setChecking(true);
    try {
      const r = await api.get(`/public/donations/${encodeURIComponent(donNo)}/payment-status`);
      setSt(r.data);
      return r.data;
    } catch {
      setSt((prev) => prev || { payment_status: "UNKNOWN" });
      return null;
    } finally { setChecking(false); }
  };

  useEffect(() => {
    if (!donNo) return;
    let alive = true;
    let count = 0;
    const poll = async () => {
      const data = await fetchStatus();
      if (!alive || !data) return;
      const ps = data.payment_status;
      count += 1;
      setAttempts(count);
      if (ps === "PENDING" && count < MAX_POLLS) {
        timer.current = setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => { alive = false; clearTimeout(timer.current); };
  }, [donNo]); // eslint-disable-line

  const ps = st?.payment_status || "PENDING";
  const stillPolling = ps === "PENDING" && attempts < MAX_POLLS;
  const timedOut = ps === "PENDING" && attempts >= MAX_POLLS;

  return (
    <div data-testid="donate-status-page">
      <SEO title="Donation Status" />
      <PageHero title="Donation Status" sub="Payment confirmation" />
      <section className="py-16">
        <div className="container-ssf max-w-xl">
          <div className="card-ssf p-10 text-center">
            {!donNo && <p className="text-sm text-slate-500">No donation reference provided.</p>}

            {donNo && stillPolling && (
              <>
                <Loader2 className="mx-auto h-12 w-12 animate-spin text-saffron-600" strokeWidth={1.5} />
                <h2 className="mt-4 font-display text-2xl font-bold text-navy" data-testid="status-verifying">Verifying payment…</h2>
                <p className="mt-2 text-sm text-slate-600">Reference: <b>{donNo}</b></p>
                <p className="mt-2 text-xs text-slate-400">Confirming securely with the payment gateway — a few seconds only.</p>
              </>
            )}

            {donNo && timedOut && (
              <>
                <Clock className="mx-auto h-12 w-12 text-saffron-600" strokeWidth={1.5} />
                <h2 className="mt-4 font-display text-2xl font-bold tracking-wide text-navy" data-testid="status-delayed">PAYMENT CONFIRMATION PENDING</h2>
                <p className="mt-2 text-sm text-slate-600">Your payment confirmation is taking longer than expected. If your account has been debited, please do not make another payment for the same donation until the status is confirmed.</p>
                <p className="mt-2 text-sm text-slate-600">Reference: <b>{donNo}</b></p>
                <div className="mt-5 flex justify-center gap-3">
                  <button onClick={() => { setAttempts(0); fetchStatus(); }} disabled={checking} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="status-check-again">
                    {checking ? <Loader2 size={14} className="animate-spin" /> : null} Check Payment Status
                  </button>
                  <Link to="/" className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="status-home-delayed"><Home size={14} /> Back to Home</Link>
                </div>
              </>
            )}

            {donNo && ps === "PAID" && (
              <>
                <CheckCircle2 className="mx-auto h-14 w-14 text-brandgreen" strokeWidth={1.5} />
                <h2 className="mt-4 font-display text-2xl font-bold tracking-wide text-brandgreen" data-testid="status-success">DONATION SUCCESSFUL</h2>
                <p className="mt-1.5 text-sm text-slate-600">Thank you for supporting Sahaj Sarathi Foundation.</p>
                <div className="mx-auto mt-4 max-w-sm rounded-lg bg-navy-50/60 p-4 text-left text-sm text-slate-700">
                  {st.name && <p><span className="text-slate-400">Donor:</span> <b>{st.name}</b></p>}
                  <p className="mt-1"><span className="text-slate-400">Amount:</span> <b>₹{Number(st.amount).toLocaleString("en-IN")}</b></p>
                  <p className="mt-1"><span className="text-slate-400">Donation No:</span> <b>{donNo}</b></p>
                  {st.receipt_no && <p className="mt-1"><span className="text-slate-400">Receipt No:</span> <b>{st.receipt_no}</b></p>}
                  {st.txn_ref && <p className="mt-1"><span className="text-slate-400">Transaction Ref:</span> <span className="font-mono text-xs">{st.txn_ref}</span></p>}
                  <p className="mt-1"><span className="text-slate-400">Status:</span> <span className="rounded-full bg-brandgreen-100 px-2 py-0.5 text-xs font-bold text-brandgreen">PAID</span></p>
                </div>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  {st.receipt_token && (
                    <Link to={`/donate/receipt/${st.receipt_token}`} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="status-view-receipt">
                      <Download size={14} /> VIEW / DOWNLOAD RECEIPT
                    </Link>
                  )}
                  {!st.receipt_token && st.receipt_ready && (
                    <a href={`/api/public/donations/${encodeURIComponent(donNo)}/receipt`} target="_blank" rel="noreferrer" className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="status-download-receipt">
                      <Download size={14} /> Download Receipt
                    </a>
                  )}
                  {st.receipt_ready && (
                    <button onClick={() => window.print()} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="status-print-receipt">
                      Print Receipt
                    </button>
                  )}
                  <Link to="/" className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="status-home-success"><Home size={14} /> Back to Home</Link>
                </div>
              </>
            )}

            {donNo && (ps === "FAILED" || ps === "USER_DROPPED") && (
              <>
                <XCircle className="mx-auto h-12 w-12 text-red-500" strokeWidth={1.5} />
                <h2 className="mt-4 font-display text-2xl font-bold tracking-wide text-navy" data-testid="status-failed">PAYMENT NOT COMPLETED</h2>
                <p className="mt-2 text-sm text-slate-600">No amount was captured for reference <b>{donNo}</b>, and no receipt has been issued.</p>
                <div className="mt-5 flex justify-center gap-3">
                  <Link to="/donate" className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="status-retry">Try Again</Link>
                  <Link to="/donate" className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="status-back-donate">Back to Donation Page</Link>
                </div>
              </>
            )}

            {donNo && ps === "REVIEW_REQUIRED" && (
              <>
                <Clock className="mx-auto h-12 w-12 text-saffron-600" strokeWidth={1.5} />
                <h2 className="mt-4 font-display text-2xl font-bold text-navy" data-testid="status-review">Payment under verification</h2>
                <p className="mt-2 text-sm text-slate-600">Your payment for reference <b>{donNo}</b> is being verified by the Foundation. Please quote this reference if you contact us.</p>
                <Link to="/" className="btn-dark mt-5 inline-flex !rounded-md !px-5 !py-2.5 text-sm" data-testid="status-home-review"><Home size={14} /> Back to Home</Link>
              </>
            )}

            {donNo && ps === "UNKNOWN" && (
              <p className="text-sm text-slate-500" data-testid="status-unknown">We could not find this donation reference. If you completed a payment, please contact the Foundation with your reference number.</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
